import {
    CreateExtArgs,
    type CreateExtensionArgument,
    DalValue,
    DmApis,
    Extension,
    ExtensionAPI,
    type ExtensionPublicAPI,
    pointerUtils,
    ValidateValue
} from '@wix/document-manager-core'
import type {
    BuilderWidgetSlot,
    Component,
    CompRef,
    InnerElementPath,
    ItemUnderXY,
    NestedDataPathSegment,
    OverrideOrDefaultData,
    PluginInfo,
    Pointer,
    PossibleViewModes,
    ScopeMetaDataTemplate,
    SlotDataType,
    WidgetSlot,
    PopulatedComponentContainerInfoMap
} from '@wix/document-services-types'
import {displayedOnlyStructureUtil, guidUtils} from '@wix/santa-core-utils'
import _ from 'lodash'
import {COMP_DATA_QUERY_KEYS, DATA_TYPES, VIEW_MODES} from '../../constants/constants'
import {ReportableError} from '@wix/document-manager-utils'
import type {ComponentsAPI} from '../components/components'
import type {DataModelExtensionAPI} from '../dataModel/dataModel'
import type {RelationshipsAPI} from '../relationships'
import type {SchemaExtensionAPI} from '../schema/schema'
import type {RemoteStructureMetaDataAPI} from '../remoteStructureMetaData'
import {getUniqueRefId, removeOuterScope, removeSharedPartsPrefix, splitReferredId} from '../../utils/inflationUtils'
import {getRefComponentChildrenWithoutCorrespondingSlots} from '../../utils/slotsUtils'
import {deepClone} from '@wix/wix-immutable-proxy'
import {getComponentToAddRef} from '../components/componentSetup'
import type {ManifestExtensionAPI} from '../builderComponents/manifest'
import {stripHashIfExists} from '../../utils/refArrayUtils'
import {createContainerInfoMapFromSlotsData} from './utils'
import {removeVariantsFromPointer} from '../../utils/variantsUtils'
// eslint-disable-next-line wix-editor/no-internal-import
import {ARCHETYPE} from '@wix/component-protocol/schema'
import type {BuilderComponentsExtensionAPI} from '../builderComponents/builderComponents'
import type {InnerElementsExtensionAPI} from '../builderComponents/innerElements'
import type {StructureExtensionAPI} from '../structure'
import {getDataValueByNestedPath} from '../builderComponents/utils'
import type {DataExtensionAPI} from '../data'
const {getPointer, getOverrideComponentPointer} = pointerUtils
const {DESKTOP, MOBILE} = VIEW_MODES
const NO_MATCH: string[] = []

export const extractPluginInfoFromComponentData = (compData: any, dal: any, pointers: any): PluginInfo | null => {
    if (!compData?.appDefinitionId || !compData?.widgetId) {
        return null
    }

    const clientSpecMapPtr = pointers.general.getClientSpecMapEntryByAppDefId(compData.appDefinitionId)
    const csmEntry = dal.get(clientSpecMapPtr)

    if (!csmEntry?.components) {
        return null
    }

    const widgetInfo = _.find(csmEntry.components, component => {
        return component?.data?.referenceComponentId === compData.widgetId
    })

    const marketData = deepClone(_.get(widgetInfo, ['data', 'marketData'], {}))

    return {
        ...marketData,
        widgetId: compData.widgetId,
        appDefinitionId: compData.appDefinitionId
    }
}

export const EVENTS = {
    SLOTS: {
        AFTER_POPULATE: 'SLOTS_AFTER_POPULATE'
    }
}

interface SlotScope {
    componentId: string
    scope: {
        componentIds: string[]
    }
}

export interface SlotPopulationContext {
    addedComponentPointer: Pointer
    slotsDataType: SlotDataType
}

export interface SlotsExtensionAPI extends ExtensionAPI {
    slots: {
        getPluginParent(childPointer: CompRef): CompRef | null
        addPopulatedComponent(
            slottedComponent: CompRef,
            componentDefinition: any,
            newCompPointerOverride?: CompRef
        ): SlotPopulationContext
        populate(
            slottedComponent: CompRef,
            slotName: string,
            componentDefinition: any,
            newCompPointerOverride?: CompRef,
            slotDataTypeOverride?: SlotDataType,
            nestedDataPath?: NestedDataPathSegment[]
        ): Pointer
        getPopulatedSlotNames(componentPointer: CompRef): string[]
        getSlotsData(componentPointer: CompRef, overrideType?: string): Record<string, CompRef>
        remove(
            slottedComponent: CompRef,
            slotName: string,
            dontDeleteComponent?: boolean,
            nestedDataPath?: NestedDataPathSegment[]
        ): {appDefinitionId?: string; widgetId?: string}
        removeSlotData(
            slottedComponent: CompRef,
            slotKeyName: string,
            dontDeleteComponent: boolean
        ): {appDefinitionId?: string; widgetId?: string}
        isChildOfSlottedComp(componentId: string): boolean
        verifySlotName(slottedComponent: CompRef, slotName: string): void
        removeFromQuery(slottedComponent: CompRef, slotName: string): void
        getWidgetSlots(widgetRef: Pointer): WidgetSlot[]
        getSlotScope(componentId: string): SlotScope
        getCompDefinition(componentPointer: CompRef): any
        getSlotsDataItem(slottedComponent: CompRef): DalValue | void
        getOwnerOfComponentInSlot(slottedComponent: CompRef): CompRef
        is: {
            unifiedContainerPlugin(pluginComponent: CompRef): boolean
            containerPlugin(pluginComponent: CompRef): boolean
        }
        getBuilderSlottedComponentOwner(populatePointer: CompRef): ItemUnderXY
        getBuilderWidgetSlots(widgetRef: Pointer): BuilderWidgetSlot[]
        getPopulatedComponentContainerInfoMap(slottedComponentPointer: CompRef): PopulatedComponentContainerInfoMap
        updateSlot(
            slottedComponent: CompRef,
            slotName: string,
            compToAddPointer: Pointer,
            slotDataTypeOverride?: SlotDataType
        ): void
        updateSlotInData(
            componentWithSlots: Pointer,
            slotName: string,
            nestedDataPathSegment: NestedDataPathSegment[],
            innerElementPath?: string[]
        ): string
    }
}

const createExtension = ({experimentInstance}: CreateExtensionArgument): Extension => {
    const createFilters = () => ({
        getSlotsFilter: (namespace: string, value: any): string[] => {
            if (namespace !== DATA_TYPES.slots || !value) {
                return NO_MATCH
            }
            if (value.slots) {
                return _.values(value.slots)
            }
            return NO_MATCH
        }
    })

    const createExtensionAPI = ({
        dal,
        extensionAPI,
        eventEmitter,
        pointers,
        coreConfig
    }: CreateExtArgs): SlotsExtensionAPI => {
        const {components} = extensionAPI as {components: ComponentsAPI}
        const {dataModel} = extensionAPI as DataModelExtensionAPI
        const {schemaAPI} = extensionAPI as SchemaExtensionAPI
        const {relationships} = extensionAPI as RelationshipsAPI
        const {data} = extensionAPI as DataExtensionAPI

        const getPluginParent = (compPointer: CompRef): CompRef | null => {
            if (!compPointer?.id) {
                return null
            }

            const slotsDataItems = dal.queryKeys(
                DATA_TYPES.slots,
                dal.queryFilterGetters.getSlotsFilter(compPointer.id)
            )
            if (slotsDataItems.length === 0) {
                return null
            }

            const slotDataPointer = slotsDataItems[0]
            if (!displayedOnlyStructureUtil.isReferredId(slotDataPointer)) {
                return null
            }

            return getOverrideComponentPointer(slotDataPointer, compPointer.type, 'slotsQuery')
        }

        const getOwnerOfComponentInSlot = (slottedComponent: CompRef): CompRef => {
            if (displayedOnlyStructureUtil.isRefPointer(slottedComponent)) {
                const rootRefHostCompId = displayedOnlyStructureUtil.getRootRefHostCompId(slottedComponent.id)
                return pointerUtils.getCompPointer(rootRefHostCompId, VIEW_MODES.DESKTOP)
            }

            return slottedComponent
        }
        const getCompDefinition = (compPointer: Pointer) => {
            const compStructure = dal.get(compPointer)
            // there is no way to read referred components from extensions, we skip validating these components intentionally because of this
            if (displayedOnlyStructureUtil.isReferredId(compPointer.id) && !compStructure) {
                const {remoteStructureMetaData} = extensionAPI as RemoteStructureMetaDataAPI
                const compType = remoteStructureMetaData.getComponentTypeFromRemoteStructure(compPointer)
                if (compType) {
                    return schemaAPI.getDefinition(compType)
                }
                throw new Error('failed to get slot type')
            }
            return schemaAPI.getDefinition(compStructure.componentType)
        }

        const getSlotProperties = (slottedComponentPointer: CompRef) => {
            const {slotsDataType} = getCompDefinition(slottedComponentPointer)
            const schema = schemaAPI.getSchema(DATA_TYPES.slots, slotsDataType)
            return _.get(schema, ['properties', 'slots', 'properties'])
        }

        const isDynamic = (compPointer: Pointer) => getCompDefinition(compPointer)?.slotsDataType === 'DynamicSlots'

        const isBuilderComponent = (compPointer: Pointer) => {
            const {builderComponents} = extensionAPI as BuilderComponentsExtensionAPI
            const structureAPI = extensionAPI as StructureExtensionAPI

            return (
                builderComponents.isBuilder(structureAPI.components.getComponentType(compPointer)) ||
                compPointer.type === DATA_TYPES.innerElements
            )
        }

        const verifySlotName = (slottedComponent: CompRef, slotName: string) => {
            const isBuilderSlots = isBuilderComponent(slottedComponent)
            const isDynamicSlots = isDynamic(slottedComponent)
            if (!isDynamicSlots && !isBuilderSlots) {
                const slotProperties = getSlotProperties(slottedComponent)
                const allowedSlotNames = _.keys(slotProperties)

                if (!allowedSlotNames.includes(slotName)) {
                    throw new Error(`Slot "${slotName}" is not a valid slot name`)
                }
            }
        }

        const getSlotsQueryRefOverrideId = (slottedComponent: CompRef) => {
            return `${slottedComponent.id}-${COMP_DATA_QUERY_KEYS.slots}`
        }

        const getSlotsDataItem = (slottedComponent: CompRef) => {
            slottedComponent = pointerUtils.getCompPointer(
                removeSharedPartsPrefix(slottedComponent.id),
                slottedComponent.type
            )

            if (
                experimentInstance.isOpen('specs.thunderbolt.DontInflateMasterPage') &&
                pointers.structure.isPartOfASharedBlock(slottedComponent)
            ) {
                slottedComponent = removeOuterScope(slottedComponent)
            }

            if (displayedOnlyStructureUtil.isReferredId(slottedComponent.id)) {
                const ownerComp = getOwnerOfComponentInSlot(slottedComponent)
                const pageId = pointers.structure.getPageOfComponent(ownerComp).id
                return dataModel.getItem(getSlotsQueryRefOverrideId(slottedComponent), DATA_TYPES.slots, pageId)
            }
            return dataModel.components.getItem(slottedComponent, DATA_TYPES.slots)
        }

        const updateSlotsDataItem = (slottedComponent: CompRef, slotsData: any): Pointer => {
            if (displayedOnlyStructureUtil.isReferredId(slottedComponent.id)) {
                const ownerComp = getOwnerOfComponentInSlot(slottedComponent)
                const pageId = pointers.structure.getPageOfComponent(ownerComp).id

                return dataModel.addItem(
                    slotsData,
                    DATA_TYPES.slots,
                    pageId,
                    getSlotsQueryRefOverrideId(slottedComponent)
                )
            }
            return dataModel.components.addItem(slottedComponent, DATA_TYPES.slots, slotsData)
        }

        const getSlotsData = (slottedComponentPointer: CompRef, overrideType?: string): Record<string, CompRef> => {
            const slotsData = getSlotsDataItem(slottedComponentPointer)
            return _.mapValues(
                slotsData?.slots,
                compId => getPointer(_.trimStart(compId, '#'), overrideType ?? slottedComponentPointer.type) as CompRef
            )
        }

        const getPopulatedSlotNames = (componentPointer: CompRef) => _.keys(getSlotsData(componentPointer))

        const verifyCanPopulate = (slotName: string, slottedComponent: CompRef) => {
            verifySlotName(slottedComponent, slotName)

            const currentSlots = getPopulatedSlotNames(slottedComponent)
            if (currentSlots.includes(slotName)) {
                coreConfig.logger.interactionStarted('slot already exist', {
                    extras: {currentSlots, slotName, slottedComponent}
                })
                throw new Error(`Slot "${slotName}" already exists`)
            }
        }

        const getSlotsDataType = (slottedComponent: CompRef, slotDataTypeOverride?: SlotDataType) => {
            if (slotDataTypeOverride) {
                return slotDataTypeOverride
            }

            const {slotsDataType} = getCompDefinition(slottedComponent)
            return slotsDataType
        }

        const updateSlot = (
            slottedComponent: CompRef,
            slotName: string,
            compToAddPointer: Pointer,
            slotDataTypeOverride?: SlotDataType
        ) => {
            verifyCanPopulate(slotName, slottedComponent)
            const slotsDataType = getSlotsDataType(slottedComponent, slotDataTypeOverride)
            const updatedData = getSlotsDataItem(slottedComponent)

            if (updatedData?.slots) {
                updatedData.slots[slotName] = compToAddPointer.id
                return updateSlotsDataItem(slottedComponent, updatedData)
            }
            return updateSlotsDataItem(slottedComponent, {
                type: slotsDataType,
                slots: {[slotName]: compToAddPointer.id}
            })
        }

        const updateSlotInData = (
            slottedComponent: Pointer,
            slotName: string,
            nestedDataPathSegment: NestedDataPathSegment[],
            innerElementPath?: string[]
        ) => {
            const slotGuid = guidUtils.getGUID()
            data.injectData({
                pointer: slottedComponent,
                nestedDataPath: nestedDataPathSegment,
                partialNewData: {[slotName]: slotGuid},
                path: innerElementPath
            })
            return slotGuid
        }

        const addPopulatedComponent = (
            slottedComponent: CompRef,
            componentDefinition: any,
            newCompPointerOverride?: CompRef
        ): SlotPopulationContext => {
            const ownerOfComponentInSlot = getOwnerOfComponentInSlot(slottedComponent)
            const componentPointer = newCompPointerOverride ?? components.getComponentToAddRef()
            const addedComponentPointer = components.addComponentV2Internal(
                componentPointer,
                ownerOfComponentInSlot,
                componentDefinition,
                {isPlugin: true, stylesPerPage: true}
            )
            const hostCompDefinition = getCompDefinition(slottedComponent)
            const slotsDataType = hostCompDefinition?.slotsDataType ?? hostCompDefinition?.builderTypes?.slots
            return {addedComponentPointer, slotsDataType}
        }

        const populate = (
            slottedComponent: CompRef,
            slotName: string,
            componentDefinition: any,
            newCompPointerOverride?: CompRef,
            slotDataTypeOverride?: SlotDataType,
            nestedDataPath?: NestedDataPathSegment[]
        ): Pointer => {
            const {addedComponentPointer, slotsDataType} = addPopulatedComponent(
                slottedComponent,
                componentDefinition,
                newCompPointerOverride
            )
            const ownerOfComponentInSlot = getOwnerOfComponentInSlot(slottedComponent)

            if (
                experimentInstance.isOpen('dm_refactorStaticSlotToDynamic') &&
                schemaAPI.isSchemaCollectionRegistered(dal.get(slottedComponent)?.componentType)
            ) {
                const slotGuid = updateSlotInData(slottedComponent, slotName, nestedDataPath ?? [])
                updateSlot(slottedComponent, slotGuid, addedComponentPointer, slotDataTypeOverride ?? slotsDataType)
            } else {
                updateSlot(slottedComponent, slotName, addedComponentPointer, slotDataTypeOverride ?? slotsDataType)
            }

            eventEmitter.emit(
                EVENTS.SLOTS.AFTER_POPULATE,
                slottedComponent,
                ownerOfComponentInSlot,
                addedComponentPointer
            )

            return addedComponentPointer
        }

        const removeFromQuery = (slottedComponent: CompRef, slotName: string) => {
            const slotsData = getSlotsDataItem(slottedComponent)
            delete slotsData.slots[slotName]
            updateSlotsDataItem(slottedComponent, slotsData)
        }

        const removeSlotData = (
            slottedComponent: CompRef,
            slotKeyName: string,
            dontDeleteComponent: boolean
        ): {appDefinitionId?: string; widgetId?: string} => {
            const compPointer = _.get(getSlotsData(slottedComponent), slotKeyName)
            const owningCompPointer = getOwnerOfComponentInSlot(slottedComponent)
            const dataItem = dataModel.components.getItem(owningCompPointer, DATA_TYPES.data) || {}
            if (!compPointer) {
                return {}
            }

            if (dal.has(compPointer) && !dontDeleteComponent) {
                components.removeComponent(compPointer)
            }

            removeFromQuery(slottedComponent, slotKeyName)

            return {appDefinitionId: dataItem.appDefinitionId, widgetId: dataItem.widgetId}
        }

        const _removeInternal = (
            slottedComponent: CompRef,
            slotName: string,
            dontDeleteComponent: boolean = false,
            nestedDataPath: NestedDataPathSegment[] = []
        ): {appDefinitionId?: string; widgetId?: string} => {
            let slotKeyName = slotName
            if (
                experimentInstance.isOpen('dm_refactorStaticSlotToDynamic') &&
                schemaAPI.isSchemaCollectionRegistered(dal.get(slottedComponent)?.componentType)
            ) {
                const componentData = data.get(slottedComponent)
                slotKeyName = getDataValueByNestedPath(nestedDataPath, componentData as Record<string, any>, slotName)
                data.injectData({pointer: slottedComponent, nestedDataPath, partialNewData: {[slotName]: ''}})
            }
            return removeSlotData(slottedComponent, slotKeyName, dontDeleteComponent)
        }

        const getComponentSlots = (componentId: string) => {
            return dal.queryKeys(DATA_TYPES.slots, dal.queryFilterGetters.getSlotsFilter(componentId))
        }

        const getBuilderSlottedComponentOwner = (populatePointer: CompRef): ItemUnderXY => {
            //base on the estimation that container can be populated to one slot only
            const slotId = getComponentSlots(populatePointer.id)[0]

            // currently support only builder component - refComponent won't work
            const parent = pointers.full.components.getParent(populatePointer)

            const ownerPointerInnerElement = relationships.getOwningReferencesToPointer(
                getPointer(slotId, DATA_TYPES.slots),
                DATA_TYPES.innerElements
            )[0]

            if (!ownerPointerInnerElement) {
                return {
                    compPointer: parent as CompRef
                }
            }

            const {referredType} = dal.get(ownerPointerInnerElement)
            const innerElementPath = referredType.split('|').slice(1)

            return {
                compPointer: parent as CompRef,
                innerElementPath
            }
        }

        const isChildOfSlottedComp = (componentId: string): boolean => {
            const slotsIds = getComponentSlots(componentId)
            return !_.isEmpty(slotsIds)
        }

        const getSlotDataFromContext = (
            metaDataForWidget: ScopeMetaDataTemplate,
            refIdContext: string
        ): WidgetSlot[] => {
            const slots: WidgetSlot[] = []
            _.forEach(metaDataForWidget.slots, slotCompObj => {
                const {compId, interfaces} = slotCompObj
                let pluginInfo = null
                const comp = _.get(metaDataForWidget.components, [compId])

                if (!comp) {
                    throw new ReportableError({
                        errorType: 'COMPONENT_FOR_SLOT_DOES_NOT_EXIST',
                        message: `comp not retrieved by '${compId}' for '${JSON.stringify(
                            slotCompObj
                        )}' during 'getSlotDataFromContext'`
                    })
                }

                const {role, componentType} = comp

                const populatedSlotCompId = getUniqueRefId(refIdContext, compId)
                const slotData = getSlotsData(pointers.getPointer(populatedSlotCompId, VIEW_MODES.DESKTOP) as CompRef)
                if (slotData) {
                    const slottedCompId = _.get(slotData, ['slot', 'id'])
                    if (slottedCompId) {
                        const compData = dataModel.components.getItem(
                            pointers.getPointer(slottedCompId, VIEW_MODES.DESKTOP),
                            DATA_TYPES.data
                        )
                        pluginInfo = extractPluginInfoFromComponentData(compData, dal, pointers)
                    }
                }
                const slotInfo: WidgetSlot = {
                    compRef: pointers.getPointer(populatedSlotCompId, VIEW_MODES.DESKTOP),
                    interfaces,
                    role,
                    componentType
                }
                if (pluginInfo) {
                    slotInfo.pluginInfo = pluginInfo
                }
                slots.push(slotInfo)
            })
            return slots
        }
        const getWidgetSlotsInner = (
            appDefinitionId: string,
            compWidgetId: string,
            possibleSlots: WidgetSlot[],
            refIdContext: string
        ) => {
            const remoteWidgetMetaDataPointer = pointers.remoteStructureMetaData.getRemoteStructureWidgetMetaData(
                appDefinitionId,
                compWidgetId
            )
            const metaDataForWidget = deepClone(dal.get(remoteWidgetMetaDataPointer))
            if (!metaDataForWidget) {
                throw new ReportableError({
                    errorType: 'REMOTE_META_DATA_WAS_NOT_LOADED',
                    message: `remote meta data and widget were not loaded`,
                    extras: {appDefinitionId, compWidgetId}
                })
            }

            const directSlots = getSlotDataFromContext(metaDataForWidget, refIdContext)
            possibleSlots.push(...directSlots)
            _.forEach(metaDataForWidget.innerWidgets, innerWidgetData => {
                const {widgetId, compId} = innerWidgetData
                const innerContext = getUniqueRefId(refIdContext, compId)
                getWidgetSlotsInner(appDefinitionId, widgetId, possibleSlots, innerContext)
            })
        }
        const getWidgetSlots = (widgetCompRef: Pointer) => {
            const widgetData = dataModel.components.getItem(widgetCompRef, DATA_TYPES.data)
            if (!widgetData) {
                throw new ReportableError({
                    errorType: 'WIDGET_NOT_FOUND',
                    message: 'comp not found or does not have data'
                })
            }
            const {appDefinitionId, widgetId} = widgetData
            const possibleSlots: WidgetSlot[] = []
            getWidgetSlotsInner(appDefinitionId, widgetId, possibleSlots, widgetCompRef.id)
            return possibleSlots
        }

        const getSlotScope = (componentId: string): SlotScope => {
            const splitIdsArr: string[] = splitReferredId(componentId)
            return {
                componentId: splitIdsArr.pop() as string,
                scope: {
                    componentIds: splitIdsArr
                }
            }
        }

        const isUnifiedContainerPlugin = (pluginCompRef: CompRef) => {
            const {manifest} = extensionAPI as ManifestExtensionAPI
            const {builderComponents} = extensionAPI as BuilderComponentsExtensionAPI
            const structureAPI = extensionAPI as StructureExtensionAPI
            const isPlugin = isChildOfSlottedComp(pluginCompRef.id)
            const parent = pointers.structure.getParent(pluginCompRef)! as CompRef
            const pluginCompType = structureAPI.components.getComponentType(pluginCompRef)
            const parentCompType = structureAPI.components.getComponentType(parent)

            return (
                isPlugin &&
                builderComponents.isBuilder(pluginCompType) &&
                builderComponents.isBuilder(parentCompType) &&
                manifest.getArchType(pluginCompType) === ARCHETYPE.Container &&
                manifest.getArchType(parentCompType) === ARCHETYPE.Container &&
                manifest.layout.getContentFill(parent)?.dataItemKey ===
                    stripHashIfExists(_.invert(getSlotsDataItem(parent).slots)[pluginCompRef.id])
            )
        }

        const isContainerPlugin = (pluginCompRef: CompRef) => {
            const {manifest} = extensionAPI as ManifestExtensionAPI
            const isPlugin = isChildOfSlottedComp(pluginCompRef.id)

            return (
                isPlugin &&
                manifest.getArchType(dal.get(pluginCompRef)?.componentType) === ARCHETYPE.Container &&
                !isUnifiedContainerPlugin(pluginCompRef)
            )
        }

        const extractPathsFromContainers = (
            containersListIncludeInnerElement: OverrideOrDefaultData[]
        ): InnerElementPath[] => {
            const paths = containersListIncludeInnerElement
                .filter(container => container.path)
                .map(container => container.path as InnerElementPath)

            return _.uniqBy(paths, path => JSON.stringify(path))
        }

        const processSlots = (
            slots: Record<string, CompRef>,
            containersListIncludeInnerElement: OverrideOrDefaultData[],
            pluginInfoArray: BuilderWidgetSlot[],
            path?: string[]
        ): void => {
            Object.entries(slots).forEach(([slotKey, slottedComponentRef]) => {
                const existingSlotContainer = containersListIncludeInnerElement.find(
                    (containerFromManifest: OverrideOrDefaultData) => containerFromManifest.value === slotKey
                )
                const compData = dataModel.components.getItem(slottedComponentRef, DATA_TYPES.data)

                const pluginInfo = extractPluginInfoFromComponentData(compData, dal, pointers)
                if (pluginInfo) {
                    const result: BuilderWidgetSlot = {
                        compRef: slottedComponentRef,
                        pluginInfo,
                        slotName: existingSlotContainer?.prop ?? slotKey,
                        innerElementPath: existingSlotContainer?.path ?? path ?? [],
                        nestedDataPath: existingSlotContainer?.nestedDataPath ?? []
                    }
                    pluginInfoArray.push(result)
                }
            })
        }

        const processRootSlots = (
            widgetCompRef: CompRef,
            containersListIncludeInnerElement: OverrideOrDefaultData[],
            pluginInfoArray: BuilderWidgetSlot[]
        ): void => {
            const slots = getSlotsData(widgetCompRef)
            processSlots(slots, containersListIncludeInnerElement, pluginInfoArray)
        }

        const processInnerElementSlots = (
            widgetCompRef: CompRef,
            pathsList: InnerElementPath[],
            containersListIncludeInnerElement: OverrideOrDefaultData[],
            pluginInfoArray: BuilderWidgetSlot[]
        ): void => {
            const {innerElements} = extensionAPI as InnerElementsExtensionAPI
            pathsList.forEach(path => {
                const innerElementSlotData = innerElements.getInnerElementSlots(widgetCompRef, path) ?? {}
                processSlots(innerElementSlotData, containersListIncludeInnerElement, pluginInfoArray, path)
            })
        }

        const getBuilderWidgetSlots = (widgetCompRef: CompRef): BuilderWidgetSlot[] => {
            const {manifest} = extensionAPI as ManifestExtensionAPI

            const containersListIncludeInnerElement = manifest
                .getData(widgetCompRef, true, [{prop: 'container'}])
                .filter(({metaData}) => metaData?.containerType === 'slot')
            const pathsList = extractPathsFromContainers(containersListIncludeInnerElement)

            const pluginInfoArray: BuilderWidgetSlot[] = []

            processRootSlots(widgetCompRef, containersListIncludeInnerElement, pluginInfoArray)
            processInnerElementSlots(widgetCompRef, pathsList, containersListIncludeInnerElement, pluginInfoArray)

            return pluginInfoArray
        }

        const getPopulatedComponentContainerInfoMap = (
            slottedComponentPointer: CompRef
        ): PopulatedComponentContainerInfoMap => {
            const {manifest} = extensionAPI as ManifestExtensionAPI

            const compRefWithoutVariants = removeVariantsFromPointer(slottedComponentPointer) as CompRef
            let manifestContainersList: OverrideOrDefaultData[] = []

            const slotsData = getSlotsDataItem(compRefWithoutVariants)
            if (schemaAPI.isSchemaCollectionRegistered(dal.get(compRefWithoutVariants)?.componentType)) {
                manifestContainersList = manifest.getData(compRefWithoutVariants, false, [{prop: 'container'}])
            }

            return createContainerInfoMapFromSlotsData(
                slotsData,
                manifestContainersList,
                getPointer,
                compRefWithoutVariants.type
            )
        }

        return {
            slots: {
                getPluginParent,
                populate,
                getPopulatedSlotNames,
                getSlotsData,
                remove: _removeInternal,
                removeSlotData,
                isChildOfSlottedComp,
                verifySlotName,
                removeFromQuery,
                getWidgetSlots,
                getSlotScope,
                getCompDefinition,
                getSlotsDataItem,
                getOwnerOfComponentInSlot,
                is: {
                    unifiedContainerPlugin: isUnifiedContainerPlugin,
                    containerPlugin: isContainerPlugin
                },
                getBuilderSlottedComponentOwner,
                getBuilderWidgetSlots,
                getPopulatedComponentContainerInfoMap,
                addPopulatedComponent,
                updateSlot,
                updateSlotInData
            }
        }
    }

    const createValidator = ({dal, pointers, extensionAPI, coreConfig}: DmApis): Record<string, ValidateValue> => {
        const getSlotOwners = (slotsQueryPointer: Pointer): Pointer[] => {
            const {relationships} = extensionAPI as RelationshipsAPI
            if (displayedOnlyStructureUtil.isRefPointer(slotsQueryPointer)) {
                const refComponentId = displayedOnlyStructureUtil.getRootRefHostCompId(slotsQueryPointer.id)

                return _.filter(
                    [getPointer(refComponentId, DESKTOP), getPointer(refComponentId, MOBILE)],
                    refCompPointer => {
                        const plugin = getPointer(slotsQueryPointer.id, refCompPointer.type)
                        return dal.has(refCompPointer) && dal.has(plugin)
                    }
                )
            }

            return relationships.getOwningReferencesToPointer(slotsQueryPointer)
        }

        const getInvalidRefComponentChildrenIds = (refComponent: CompRef): string[] =>
            getRefComponentChildrenWithoutCorrespondingSlots(dal, pointers, refComponent).map(child => child.id)

        return {
            invalidRefComponentChildren: (pointer: Pointer, value: DalValue) => {
                if (!_.includes(Object.values(VIEW_MODES), pointer.type)) {
                    return
                }

                const component: Component | undefined = dal.get(pointer)

                if (component?.type !== 'RefComponent') return

                const invalidRefCompChildren = getInvalidRefComponentChildrenIds(pointer as CompRef)

                if (invalidRefCompChildren.length === 0) return

                coreConfig.logger.captureError(
                    new ReportableError({
                        message: 'invalidRefCompChildren',
                        errorType: 'invalidRefCompChildren',
                        extras: {
                            pointer,
                            value
                        }
                    })
                )
                return [
                    {
                        shouldFail: true,
                        type: 'invalidRefComponentChildren',
                        message: 'Invalid components inside refComponent',
                        extras: {
                            invalidRefCompChildren
                        }
                    }
                ]
            },
            invalidSlotQuery: (pointer: Pointer) => {
                if (pointer.type === DATA_TYPES.slots && displayedOnlyStructureUtil.isRefPointer(pointer)) {
                    const refCompId = displayedOnlyStructureUtil.getRefHostCompId(pointer.id)

                    const invalidRefCompChildren = _.uniq(
                        Object.values(VIEW_MODES).flatMap((viewMode: PossibleViewModes) =>
                            getInvalidRefComponentChildrenIds(pointerUtils.getCompPointer(refCompId, viewMode))
                        )
                    )

                    if (invalidRefCompChildren.length === 0) return

                    return [
                        {
                            shouldFail: true,
                            type: 'invalidSlotQuery',
                            message:
                                'Reference to plugin component was removed from slots data item, but was not removed from the RefComponent children',
                            extras: {
                                invalidRefCompChildren
                            }
                        }
                    ]
                }
            },
            invalidSlotsReference: (pointer: Pointer, value: DalValue) => {
                if (pointer.type === DATA_TYPES.slots && !_.isEmpty(value?.slots)) {
                    const referencedCompsIds = value.slots as Record<string, string>
                    const slotOwners = getSlotOwners(pointer)
                    const ownersFromDal = _.map(slotOwners, ownerPtr => dal.get(ownerPtr))
                    const missingCompsIds = _.pickBy(referencedCompsIds, refId =>
                        _.some(
                            _.reject(ownersFromDal, {type: 'InnerElement'}),
                            owner => !_.includes(owner.components, refId)
                        )
                    )
                    if (!_.isEmpty(missingCompsIds)) {
                        return [
                            {
                                shouldFail: true,
                                type: 'invalidSlotsReference',
                                message: 'Invalid slots reference to one or more of its components',
                                extras: {
                                    missingCompsIds
                                }
                            }
                        ]
                    }
                }
            }
        }
    }

    const createPublicAPI = ({extensionAPI, dal}: DmApis): ExtensionPublicAPI => {
        const {slots} = extensionAPI as SlotsExtensionAPI
        const {manifest} = extensionAPI as ManifestExtensionAPI
        const {schemaAPI} = extensionAPI as SchemaExtensionAPI
        return {
            slots: {
                getComponentToAddRef: (containerPointer: CompRef) => getComponentToAddRef(containerPointer),
                remove: (
                    slottedComponent: CompRef,
                    slotName: string,
                    dontDeleteComponent?: boolean,
                    nestedDataPath: NestedDataPathSegment[] = []
                ) => {
                    slots.verifySlotName(slottedComponent, slotName)
                    const componentType = dal.get(slottedComponent)?.componentType
                    const isBuilder = schemaAPI.isSchemaCollectionRegistered(componentType)
                    if (experimentInstance.isOpen('dm_refactorStaticSlotToDynamic') && isBuilder) {
                        manifest.validateContainerTypeForRemove(slottedComponent, slotName, nestedDataPath)
                    }
                    return slots.remove(slottedComponent, slotName, dontDeleteComponent, nestedDataPath)
                }
            }
        }
    }

    return {
        name: 'slots',
        createFilters,
        createValidator,
        createExtensionAPI,
        createPublicAPI
    }
}

export {createExtension}
